/**
 * Classes service — Firestore CRUD for the `classes` collection (scheduled
 * one-to-one sessions between a student and a mentor).
 *
 * A class document:
 *   {
 *     title, subject,
 *     studentId, mentorId,
 *     enrollmentId,            // the course this session belongs to. THE join —
 *                              // progress, session logs and the course profile
 *                              // all match on it. '' for a standalone session
 *                              // booked outside any enrollment.
 *     startAt: Timestamp,       // session start (absolute UTC instant)
 *     durationMin: number,
 *     timezone: string,        // IANA-labelled tz the session was scheduled in
 *                              // (e.g. "Asia/Kolkata (IST)") — for display only;
 *                              // `startAt` is always the unambiguous instant.
 *     status: 'scheduled' | 'completed' | 'cancelled'
 *           | 'not_conducted' | 'mentor_no_show' | 'both_no_show'
 *           | 'student_no_show',
 *     meetingLink, notes,
 *     createdAt, updatedAt
 *   }
 *
 * "Upcoming" vs "completed" stats are derived from `startAt`/`status` — see
 * the dashboard stats hook.
 */
import firestoreService, { orderBy, where, limit, Timestamp, serverTimestamp, increment } from './firestore.service';

const CLASSES = 'classes';
// Session logs share their doc id with the class (one log per class), so class
// deletion can clean up the log without a query.
const SESSION_LOGS = 'sessionLogs';
const BULK_TIME_CHANGES = 'bulkTimeChanges';

export const CLASS_STATUS = Object.freeze({
  SCHEDULED: 'scheduled',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  // The mentor was THERE and the session still couldn't run — a tech failure,
  // or the student left immediately. Written only by a submitted session log
  // carrying `wasConducted: false`. Nobody is at fault and no penalty applies.
  NOT_CONDUCTED: 'not_conducted',
  // The mentor never joined, or joined more than GRACE_LATE_MIN late. Stamped
  // by the attendance pass; carries the ₹500 penalty.
  //
  // Deliberately distinct from `not_conducted`, which it used to share: one is
  // a broken session, the other a broken promise, and collapsing them meant an
  // admin scanning the class list could not tell a flaking mentor from a
  // diligent one. The derived `SESSION_STATUS` always drew this distinction
  // (features/payouts/constants.js) — only the stored status lagged.
  MENTOR_NO_SHOW: 'mentor_no_show',
  // Nobody turned up. Distinct from `mentor_no_show` because the penalty
  // hinges on it: the ₹500 compensates a student who was left waiting, so with
  // no student there is nobody to compensate and nothing is charged. The
  // mentor still earns ₹0 and the session is still consumed.
  BOTH_NO_SHOW: 'both_no_show',
  // The mentor turned up and the student didn't. Distinct again because the
  // money differs: the mentor earns the no-show compensation.
  // Stamped by the attendance pass ~20 min after the start.
  STUDENT_NO_SHOW: 'student_no_show',
});

/** Convert a datetime-local string (or Date) into a Firestore Timestamp. */
function toTimestamp(value) {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
}

function sanitize(input = {}) {
  return {
    title: (input.title || '').trim(),
    subject: input.subject || '',
    studentId: input.studentId || '',
    mentorId: input.mentorId || '',
    // The enrollment this session teaches. Everything that needs to know
    // "which course was this?" reads this id, never the subject label.
    enrollmentId: input.enrollmentId || '',
    startAt: toTimestamp(input.startAt),
    durationMin: Number(input.durationMin) || 60,
    timezone: input.timezone || '',
    status: input.status || CLASS_STATUS.SCHEDULED,
    meetingLink: (input.meetingLink || '').trim(),
    notes: (input.notes || '').trim(),
  };
}

export const classesService = {
  subscribe(onData, onError) {
    return firestoreService.subscribeCollection(
      CLASSES,
      [orderBy('startAt', 'asc')],
      onData,
      onError
    );
  },

  list() {
    return firestoreService.getCollection(CLASSES, [orderBy('startAt', 'asc')]);
  },

  /**
   * Realtime subscription to the next `n` upcoming sessions (scheduled and
   * starting from now), ordered by start time. Bounded server-side with `limit`
   * so the admin dashboard never streams the whole (ever-growing) collection.
   * Requires the composite index (status ASC, startAt ASC) in firestore.indexes.json.
   */
  subscribeUpcoming(n, onData, onError) {
    return firestoreService.subscribeCollection(
      CLASSES,
      [
        where('status', '==', CLASS_STATUS.SCHEDULED),
        where('startAt', '>=', Timestamp.now()),
        orderBy('startAt', 'asc'),
        limit(n),
      ],
      onData,
      onError
    );
  },

  /** Server-side count of upcoming (scheduled, future) classes. */
  countUpcoming() {
    return firestoreService.count(CLASSES, [
      where('status', '==', CLASS_STATUS.SCHEDULED),
      where('startAt', '>=', Timestamp.now()),
    ]);
  },

  /** Server-side count of completed classes. */
  countCompleted() {
    return firestoreService.count(CLASSES, [where('status', '==', CLASS_STATUS.COMPLETED)]);
  },

  /**
   * Server-side count per settled outcome, shaped like a `tallyOutcomes` result
   * so the admin dashboard can render the same strip as everywhere else.
   *
   * One count aggregation per status rather than downloading the collection —
   * the whole point of this hook's design (see useDashboardStats). Aggregations
   * bill a fraction of a read each, so this stays cheap as history grows, which
   * a `useCollection(classesService)` would not.
   */
  async countOutcomes() {
    const of = (status) => firestoreService.count(CLASSES, [where('status', '==', status)]);
    const [completed, studentMissed, mentorMissed, nobodyJoined, notConducted, cancelled] =
      await Promise.all([
        of(CLASS_STATUS.COMPLETED),
        of(CLASS_STATUS.STUDENT_NO_SHOW),
        of(CLASS_STATUS.MENTOR_NO_SHOW),
        of(CLASS_STATUS.BOTH_NO_SHOW),
        of(CLASS_STATUS.NOT_CONDUCTED),
        of(CLASS_STATUS.CANCELLED),
      ]);
    const held = completed + studentMissed + mentorMissed + nobodyJoined + notConducted;
    return {
      total: held + cancelled,
      held,
      completed,
      studentMissed,
      mentorMissed,
      nobodyJoined,
      notConducted,
      cancelled,
      upcoming: 0, // counted separately by countUpcoming()
    };
  },

  get(id) {
    return firestoreService.getDocument(CLASSES, id);
  },

  /**
   * Book a single session. Scheduling a class is itself a student↔mentor
   * pairing; the student's denormalized mentor snapshot is re-derived by the
   * `onClassLinksChanged` Cloud Function, so nothing here has to remember to.
   */
  create(data) {
    return firestoreService.addDocument(CLASSES, sanitize(data));
  },

  /**
   * Create a recurring series in one atomic batch. `common` holds the shared
   * fields (title, student, mentor, subject, duration, timezone, link, notes);
   * `occurrences` is the list of absolute start instants (Date|ms) produced by
   * generateWeeklyOccurrences. All instances share a `seriesId`; only the first
   * (`seriesAnchor`) is flagged to notify — the Cloud Function then sends a
   * single summary email covering the whole series instead of one per class.
   * Returns the seriesId.
   */
  async createSeries(common, occurrences, titles = []) {
    const base = sanitize(common);
    // `seriesId` identifies THIS BATCH of classes, not the course. The two are
    // deliberately separate ids: `enrollmentId` is the course (it owns
    // progress, session logs and the quota bucket) and outlives any number of
    // batches, while a batch is what `onClassCreated` gathers for one summary
    // email and what "session 3 of 12" counts within. Collapsing them would
    // make a later top-up email list every session already taught.
    const seriesId = `series_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const total = occurrences.length;
    const docs = occurrences.map((when, i) => ({
      ...base,
      ...(titles[i]?.trim() ? { title: titles[i].trim() } : {}),
      startAt: when instanceof Timestamp ? when : Timestamp.fromDate(new Date(when)),
      seriesId,
      seriesIndex: i,
      seriesTotal: total,
      seriesAnchor: i === 0,
      notify: i === 0,
    }));
    await firestoreService.createManyDocuments(CLASSES, docs);
    return seriesId;
  },

  /**
   * Stamp the moment a participant joined a class. `role` is 'student' or
   * 'mentor'. Uses `serverTimestamp()` so the recorded join time is the
   * authoritative server clock (not the client's, which can be skewed) — this
   * is what the attendance rules judge punctuality against. Write-once is
   * enforced by the caller (only called when the field is still empty) and, for
   * students, narrowed by Firestore rules to this one field on their own class.
   */
  recordJoin(id, role) {
    const field = role === 'mentor' ? 'mentorJoinedAt' : 'studentJoinedAt';
    return firestoreService.updateDocument(CLASSES, id, { [field]: serverTimestamp() });
  },

  /** Edit a class. May move it to a different mentor; the trigger re-derives. */
  update(id, data) {
    return firestoreService.updateDocument(CLASSES, id, sanitize(data));
  },

  /**
   * Realtime subscription to a single mentor's classes. Filtered server-side by
   * `mentorId` so a mentor only ever receives their own sessions (enforced again
   * by Firestore rules). Sorted client-side by start time to avoid needing a
   * composite index on (mentorId, startAt).
   */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      CLASSES,
      [where('mentorId', '==', mentorId)],
      (rows) => onData(sortByStart(rows)),
      onError
    );
  },

  /**
   * Realtime subscription to a single student's classes. Filtered server-side by
   * `studentId` so a student only ever receives their own sessions (enforced
   * again by Firestore rules). Sorted client-side by start time to avoid needing
   * a composite index on (studentId, startAt).
   */
  subscribeByStudent(studentId, onData, onError) {
    return firestoreService.subscribeCollection(
      CLASSES,
      [where('studentId', '==', studentId)],
      (rows) => onData(sortByStart(rows)),
      onError
    );
  },

  /** Flip a class to a new lifecycle status (e.g. mark completed/cancelled). */
  setStatus(id, status) {
    return firestoreService.updateDocument(CLASSES, id, { status });
  },

  /**
   * Record the admin's decision on a detected mentor no-show penalty.
   *
   * The no-show itself is derived automatically from the join stamps, but the
   * ₹500 is only deducted once an admin confirms it here (from /admin/disputes)
   * — attendance is judged on the in-app Join click, so an honest mentor who
   * forgot to click it must not be fined by a robot. `deriveSessionLog` reads
   * this field; a class without one is treated as pending and charges nothing.
   *
   * Admin-only (enforced by the `classes` rules — mentors cannot touch this key).
   *
   * @param {string} id
   * @param {'pending'|'confirmed'|'waived'} status
   * @param {{ id?: string, name?: string, reason?: string }} [actor]
   */
  setNoShowPenalty(id, status, actor = {}) {
    return firestoreService.updateDocument(CLASSES, id, {
      noShowPenalty: noShowDecision(status, actor),
    });
  },

  /**
   * Same decision applied to many classes at once — the admin's "settle the
   * backlog up to <date>" action on the no-show review queue. Batched (chunked
   * at 450 ops) so a long history of unreviewed no-shows commits in one pass.
   *
   * @param {string[]} ids
   * @param {'pending'|'confirmed'|'waived'} status
   * @param {{ id?: string, name?: string, reason?: string }} [actor]
   */
  async setNoShowPenaltyMany(ids = [], status, actor = {}) {
    const targets = [...new Set(ids.filter(Boolean))];
    if (targets.length === 0) return 0;
    const decision = noShowDecision(status, actor);
    await firestoreService.updateManyDocuments(
      CLASSES,
      targets.map((id) => ({ id, data: { noShowPenalty: decision } }))
    );
    return targets.length;
  },

  /**
   * Move a class to a new instant. Updates only the timing fields and stamps it
   * `rescheduled` (drives the purple calendar colour) — without touching the
   * student/mentor/notes. The class is (re)activated to 'scheduled' so a
   * previously-cancelled, missed session comes back to life. The matching
   * `rescheduleHistory` record (written separately) is what triggers the
   * notification emails; this method intentionally does not send anything.
   *
   * When the reschedule is recorded on someone's behalf (admin attribution),
   * `countFor` also bumps that party's per-class counter — the same field the
   * scheduling callables increment, so quota meters read identically.
   *
   * @param {string} id
   * @param {{ startAt: Date|number|Timestamp, timezone?: string, countFor?: 'student'|'mentor' }} next
   */
  reschedule(id, { startAt, timezone, countFor }) {
    return firestoreService.updateDocument(CLASSES, id, {
      startAt: toTimestamp(startAt),
      timezone: timezone || '',
      status: CLASS_STATUS.SCHEDULED,
      rescheduled: true,
      lastRescheduledAt: serverTimestamp(),
      ...(countFor === 'student' ? { studentRescheduleCount: increment(1) } : {}),
      ...(countFor === 'mentor' ? { mentorRescheduleCount: increment(1) } : {}),
    });
  },

  /**
   * Bulk time change — move many classes to new instants in one atomic write.
   * Used by the admin "change time for all upcoming classes" flow: `updates`
   * is `[{ id, startAt, timezone, oldStartAt, oldTimezone, title, subject,
   * studentId, mentorId, durationMin }]`. Only the timing fields are touched
   * on each class, so titles/links/notes survive, and every subscriber
   * (mentor dashboard, student dashboard, calendars) picks the change up in
   * realtime from the shared `classes` collection.
   *
   * After the updates commit, one `bulkTimeChanges` record is written carrying
   * the old → new instants per class — the Cloud Function trigger on that
   * collection fans out a single summary email per student/mentor pair (the
   * same pattern series creation uses to avoid one email per class).
   */
  async bulkUpdateTimes(updates) {
    await firestoreService.updateManyDocuments(
      CLASSES,
      updates.map(({ id, startAt, timezone }) => ({
        id,
        data: {
          startAt: toTimestamp(startAt),
          ...(timezone ? { timezone } : {}),
        },
      }))
    );
    await firestoreService.addDocument(BULK_TIME_CHANGES, {
      changedBy: 'admin',
      timezone: updates[0]?.timezone || '',
      count: updates.length,
      changes: updates.map((u) => ({
        classId: u.id,
        title: u.title || '',
        subject: u.subject || '',
        studentId: u.studentId || '',
        mentorId: u.mentorId || '',
        durationMin: Number(u.durationMin) || 60,
        oldStartAt: toTimestamp(u.oldStartAt),
        newStartAt: toTimestamp(u.startAt),
        oldTimezone: u.oldTimezone || '',
        newTimezone: u.timezone || '',
      })),
    });
  },

  /**
   * Attach an uploaded note/material to a class. `material` is the metadata
   * object returned by storageService.uploadClassNote (name, url, path, …).
   * Uses arrayUnion so concurrent uploads don't clobber each other, and bypasses
   * `sanitize` so the rest of the class document is untouched.
   */
  addMaterial(id, material) {
    return firestoreService.addToArray(CLASSES, id, 'materials', material);
  },

  /** Detach a previously uploaded material (pass the stored object verbatim). */
  removeMaterial(id, material) {
    return firestoreService.removeFromArray(CLASSES, id, 'materials', material);
  },

  /**
   * Admin: permanently delete a class *and* its session log (sessionLogs doc id
   * === classId) so the session never lingers in payout ledgers, reports, or
   * the mentor's log list. Deleting a log that was never stubbed is a no-op.
   */
  async remove(id) {
    await firestoreService.deleteDocument(CLASSES, id);
    await firestoreService.deleteDocument(SESSION_LOGS, id);
  },

  /**
   * Admin: bulk variant of `remove` — batched, classes first then their logs.
   * Deleting the last class linking a student to a mentor ends that pairing;
   * the delete trigger re-derives the snapshot, so no student list is needed.
   */
  async removeMany(ids) {
    if (!ids?.length) return;
    await firestoreService.deleteManyDocuments(CLASSES, ids);
    await firestoreService.deleteManyDocuments(SESSION_LOGS, ids);
  },
};

/**
 * The stamped admin decision on a mentor-no-show penalty. `Timestamp.now()` (not
 * serverTimestamp) so the same object can be reused across a batch write.
 */
function noShowDecision(status, actor = {}) {
  return {
    status,
    decidedBy: actor.id || '',
    decidedByName: actor.name || '',
    reason: (actor.reason || '').trim(),
    decidedAt: Timestamp.now(),
  };
}

/** Chronological sort by `startAt`, tolerant of missing/!Timestamp values. */
function sortByStart(rows) {
  return [...rows].sort((a, b) => {
    const at = a.startAt?.toDate?.()?.getTime?.() ?? 0;
    const bt = b.startAt?.toDate?.()?.getTime?.() ?? 0;
    return at - bt;
  });
}

export default classesService;
